import { useState } from "react";
import { Wifi } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import OAuthButton from "@/components/auth/OAuthButton";
import { trackHomeCtaClick } from "@/lib/analytics";

export interface LandingValueSignupProps {
  mode: "signIn" | "signUp";
  setMode: (mode: "signIn" | "signUp") => void;
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  formLoading: boolean;
  hasConnection: boolean;
  isOnline: boolean;
  onOAuth: (provider: "google" | "apple") => void;
  onPasswordSubmit: (e: React.FormEvent) => void;
  onMagicLink: () => void;
  onGuest: () => void;
}

/**
 * The single conversion block: value proposition + frictionless signup.
 * Email signup is passwordless (magic link); passwords only appear when an
 * existing member switches to sign-in.
 */
export const LandingValueSignup = ({
  mode,
  setMode,
  email,
  setEmail,
  password,
  setPassword,
  formLoading,
  hasConnection,
  isOnline,
  onOAuth,
  onPasswordSubmit,
  onMagicLink,
  onGuest,
}: LandingValueSignupProps) => {
  const [showEmail, setShowEmail] = useState(false);
  const isSignUp = mode === "signUp";

  return (
    <section id="signup" className="mx-auto w-full max-w-xl scroll-mt-24 text-center">
      {isSignUp ? (
        <div className="space-y-3 text-left">
          <OAuthButton
            provider="google"
            label="Sign up with Google"
            onClick={() => onOAuth("google")}
            disabled={!hasConnection || formLoading}
          />
          <OAuthButton
            provider="apple"
            label="Sign up with Apple"
            onClick={() => onOAuth("apple")}
            disabled={!hasConnection || formLoading}
          />

          {!showEmail ? (
            <div className="pt-1 text-center">
              <button
                type="button"
                className="text-sm font-medium text-background underline underline-offset-4 decoration-2 hover:decoration-primary transition-colors"
                onClick={() => {
                  trackHomeCtaClick("signup_email_reveal", "#signup");
                  setShowEmail(true);
                }}
              >
                Or, sign up with email
              </button>
            </div>
          ) : (
            <form
              className="pt-1 space-y-2"
              onSubmit={(e) => {
                e.preventDefault();
                onMagicLink();
              }}
            >
              <Label htmlFor="signup-email" className="text-sm text-background/80">
                Email address
              </Label>
              <div className="flex gap-2">
                <Input
                  id="signup-email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  className="bg-background"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <Button
                  type="submit"
                  disabled={formLoading || !hasConnection}
                  className="bg-primary hover:bg-primary-hover text-primary-foreground bg-none shrink-0"
                >
                  {formLoading ? "Sending..." : "Continue"}
                </Button>
              </div>
              <p className="text-xs text-background">
                We'll email you a link to sign in. No password needed.
              </p>
            </form>
          )}
        </div>
      ) : (
        <div className="space-y-3 text-left">
          <OAuthButton
            provider="google"
            onClick={() => onOAuth("google")}
            disabled={!hasConnection || formLoading}
          />
          <OAuthButton
            provider="apple"
            onClick={() => onOAuth("apple")}
            disabled={!hasConnection || formLoading}
          />

          <div className="flex items-center gap-3 pt-1">
            <span className="h-px flex-1 bg-background/50" />
            <span className="text-xs uppercase tracking-wide text-background">or</span>
            <span className="h-px flex-1 bg-background/50" />
          </div>

          <form onSubmit={onPasswordSubmit} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="signin-email" className="text-background">Email</Label>
              <Input
                className="bg-background"
                id="signin-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="signin-password" className="text-background">Password</Label>
              <Input
                className="bg-background"
                id="signin-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button
              type="submit"
              variant="outline"
              className="w-full bg-background/10 text-background hover:bg-background hover:text-foreground"
              disabled={formLoading || !hasConnection}
            >
              {formLoading ? "Please wait..." : "Sign in"}
            </Button>
            <div className="text-center">
              <button
                type="button"
                className="text-sm font-medium text-background underline underline-offset-4 decoration-2 hover:decoration-primary transition-colors"
                onClick={onMagicLink}
                disabled={formLoading}
              >
                Forgot password? Send magic link
              </button>
            </div>
          </form>
        </div>
      )}


      {!hasConnection && (
        <div className="mt-3 flex items-center justify-center text-sm text-background">
          <Wifi className="mr-2 h-4 w-4" />
          {!isOnline ? "No internet connection" : "Connecting to server..."}
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm font-medium text-background">
        <button
          type="button"
          className="underline underline-offset-4 decoration-2 hover:decoration-primary transition-colors"
          onClick={() => {
            setShowEmail(false);
            setMode(isSignUp ? "signIn" : "signUp");
          }}
        >
          {isSignUp ? "Already a member? Sign in" : "New here? Create an account"}
        </button>
        <span aria-hidden="true">·</span>
        <button
          type="button"
          className="underline underline-offset-4 decoration-2 hover:decoration-primary transition-colors"
          onClick={onGuest}
        >
          Continue as guest
        </button>
      </div>
    </section>
  );
};

export default LandingValueSignup;
